import { Box, CircularProgress, Typography, useTheme } from '@mui/material'
import React, { useState } from 'react'
import { observer } from 'mobx-react-lite'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'

import { convertToCDNLink, formatVideoDuration, Icons } from '../../../utils/utilities'
import { useOnlineSessionsStore } from '../../../store/onlinesessions.store'
import { useQuizAndQuestionbankStore } from '../../../store/quiz.store'
import CustomChip from '../../../components/common/CustomChip'
import Progress from '../../../components/common/Progress'
import CustomImage from '../../../shared/components/common/CustomImage'
import api from '../../../service/api'
import { useSettingsStore } from '../../../store/settings.store'
import { isUrlAccessible } from '../../../shared/utils/utilities'

const HybridClassVideo = observer( ( { loadingVideo, video, playVideo, videoProgresses = {} } ) => {


    const [loadingVideoAndQuizDetails, setLoadingVideoAndQuizDetails] = useState( false )

    const OnlineSessionsStore = useOnlineSessionsStore()
    const QuizStore = useQuizAndQuestionbankStore()
    const SettingsStore = useSettingsStore()

    const meeting = OnlineSessionsStore.getSessionMetadata

    const { palette, border } = useTheme()

    const play = async () => {

        if ( !meeting.started )
            return toast( "Please wait for the class to start." )

        const getSignedUrl = async ( key, legacy ) => {
            const { data: { data: url } } = await api.recording.startWatchingHybridClassRecording( video.hybrid_class_id, video.id, { key, register_watch: true, legacy } )
            return url
        }

        try {
            setLoadingVideoAndQuizDetails( true )

            const key = video.recording_key || video.file_key

            let url

            const streamSource = SettingsStore.getSettingValueFromKey( 'hybrid_class_stream_source', 'spaces' )
            const signedUrl = await getSignedUrl( key, !!video.file_key )

            if ( streamSource === 'cdn' ) {
                const cdnUrl = convertToCDNLink( video?.recording_metadata?.location )
                if ( await isUrlAccessible( cdnUrl ) ) {
                    url = cdnUrl
                } else {
                    url = signedUrl
                }
            } else {
                try {
                    url = signedUrl
                } catch ( err ) {
                    console.log( err )
                    return
                }
            }

            const quizInstance = QuizStore.getClassQuizzes[meeting.time_table_id]?.at( 0 )

            const params = {}
            params.class_type = "hybrid-class"
            params.hybrid_class_id = video.hybrid_class_id

            const videoDetails = { ...video }
            if ( quizInstance && quizInstance.quiz_id && quizInstance.id ) {
                await QuizStore.fetchQuizInstanceForStudent( quizInstance.quiz_id, quizInstance.id )
                const attemptInstanceDetails = QuizStore.getQuizInstance

                if ( +attemptInstanceDetails.attempted === 0 ) {
                    try {
                        if ( !attemptInstanceDetails.attempt_id ) {
                            const { data: { data: { attempt_id } } } = await api.quiz.startQuiz( quizInstance.quiz_id, quizInstance.id, params )
                            const [{ success: refreshSuccess, message: refreshMessage } = {}, { success: questionSuccess, message: questionsMessage } = {}] = await Promise.all( [
                                QuizStore.fetchQuizInstanceForStudent( quizInstance.quiz_id, quizInstance.id ),
                                QuizStore.fetchQuizQuestionsForAttempt( quizInstance.quiz_id, quizInstance.id, attempt_id )
                            ] )
                            if ( !refreshSuccess || !questionSuccess ) {
                                return toast( refreshMessage || questionsMessage || "Something went wrong while getting quiz questions." )
                            }
                        } else {
                            const { success: questionSuccess, message: questionsMessage } = await QuizStore.fetchQuizQuestionsForAttempt( quizInstance.quiz_id, quizInstance.id, attemptInstanceDetails.attempt_id )
                            if ( !questionSuccess ) {
                                return toast( questionsMessage || "Something went wrong while getting quiz questions." )
                            }
                        }
                        videoDetails.quiz_completed = false
                        videoDetails.questions = QuizStore.getQuizAttemptQuestions
                    } catch ( err ) {
                        console.log( "Quiz start failed", err )

                        videoDetails.quiz_completed = true
                        videoDetails.video_timeline_events = []
                    }
                } else {
                    videoDetails.quiz_completed = true
                    videoDetails.video_timeline_events = []
                }
            } else {
                videoDetails.quiz_completed = true
                videoDetails.video_timeline_events = []
            }

            videoDetails.video_timeline_events = ( videoDetails.video_timeline_events || [] )?.filter( e => {
                if ( e?.data?.question )
                    return !QuizStore.getAttemptingAnswers[e?.data?.question]
                else {
                    const q = QuizStore.getQuizAttemptQuestions[e.eventIndex]
                    return !QuizStore.getAttemptingAnswers[q.id]
                }
            } )
            await playVideo( url, videoDetails )
        } catch ( err ) {
            console.log( err )
            toast( err?.response?.data?.message || "Something went wrong while fetching video!" )
        } finally {
            setLoadingVideoAndQuizDetails( false )
        }
    }

    return (
        <Box component="button" disabled={loadingVideoAndQuizDetails || !meeting.started} onClick={() => play()} border={border[1]} sx={{ textAlign: "left", cursor: "pointer", userSelect: "none", background: palette.contentBg, ":hover": { bgcolor: palette.action.hover } }} justifyContent="space-between" flexWrap="wrap" padding="5px 10px" display="flex" alignItems="center" gap="10px" borderRadius="5px" bgcolor={palette.contentBg}>
            <Box sx={{ flexDirection: { xs: "column", sm: "row" } }} display="flex" flexGrow={1} gap="10px" >


                <Box border={border[1]} borderRadius="5px" overflow="clip" sx={{ aspectRatio: "16 / 9" }} height="120px" position="relative">
                    {video?.thumbnail_metadata?.location ?
                        <CustomImage src={video.thumbnail_metadata?.location} /> :
                        <Box display="flex" alignItems="center" justifyContent="center" height="100%" bgcolor={palette.contentBg} color="text.secondary" fontSize="32px">
                            {Icons.default.OndemandVideoIcon}
                        </Box>
                    }
                    <Box position="absolute" display="flex" alignItems="center" justifyContent="center" sx={{ bottom: 10, left: 10, color: palette.text.primary, cursor: "pointer", }} fontSize="40px" component="span" >
                        <Box sx={{ display: "flex", gap: 1, alignItems: "center", bgcolor: palette.grey[700], p: "2px 10px 2px 2px", borderRadius: 5 }}>

                            {loadingVideoAndQuizDetails ?
                                <CircularProgress color='greyed' size={30} /> :
                                videoProgresses[video.id] !== 100 ?
                                    <Progress progressTextStyles={{ fontSize: "14px", color: palette.common.white, }} secondaryColor='white' color={palette.secondary.main} indicatorWidth={3} trackWidth={3} size={30} showProgress progress={videoProgresses[video.id] ?? 0} />
                                    :
                                    <Typography color='success' fontSize="30px" component="span" sx={{ height: "fit-content", display: "inline-flex", alignItems: "center", }}>
                                        {Icons.default.CheckCircleIconOutlined}
                                    </Typography>
                            }

                            <Typography sx={{ color: palette.common.white, fontSize: "0.75rem" }}>Watched</Typography>
                        </Box>
                    </Box>
                    {video.recording_metadata?.duration && <Box position="absolute" display="flex" alignItems="center" justifyContent="center" sx={{ bottom: 2, right: 2, color: palette.text.primary }}   >
                        <Typography fontSize="10px" borderRadius="5px" padding="1px 3px" display="flex" alignItems="center" justifyContent="center" bgcolor={palette.contentBg}>{formatVideoDuration( video.recording_metadata.duration )}</Typography>
                    </Box>}
                </Box>

                {/* {videoProgresses[video.id] === 100 && <Typography color='success' fontSize="20px">
                    {Icons.default.CheckCircleIconOutlined}
                </Typography>} */}
                <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", justifyContent: "flex-start" }}>
                    <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <Typography className='truncate-at-2' gutterBottom sx={{ fontSize: "1rem", fontWeight: 600 }} component="h3">
                            {video.title}
                        </Typography>
                        {video.expired && <CustomChip variant="semifilled" color="error" value="Expired" />}
                    </Box>
                    <Box sx={{ mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.secondary", fontSize: "0.75rem" }} >TOPICS COVERED</Typography>
                        <Box gap="5px" alignItems="center" display="flex" flexWrap="wrap">
                            {video.topics_covered?.map( topic => (
                                <CustomChip key={topic} value={topic} />
                            ) )}
                        </Box>
                        {( !video.topics_covered || video.topics_covered.length === 0 ) && <Typography variant='subtitle2' color="textDisabled" component="em" sx={{ fontSize: "0.75rem" }} >Topics not available</Typography>}
                    </Box>
                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.secondary", fontSize: "0.75rem", mb: 1 }} >UPLOAD DETAILS</Typography>
                        <Box display={{ xs: "none", md: "flex" }} gap="5px" alignItems="center" flexWrap="wrap">
                            {videoProgresses[video.id] !== 100 && video.expired && <CustomChip tooltipPlacement='bottom' color="error" variant='semifilled' value={`Video expired, Expired at: ${dayjs( video.endDate ).format( "LLL" )}`} />}
                            {videoProgresses[video.id] !== 100 && !video.expired && <CustomChip tooltipPlacement='bottom' color="error" variant='filled' tooltipTitle='Complete watching the video before it expires to get your attendance' value={`Pending Completion, Expires at: ${dayjs( video.endDate ).format( "LLL" )}`} />}
                            {videoProgresses[video.id] === 100 && <CustomChip color="success" variant='filled' value="Watched" />}
                        </Box>
                    </Box>
                    <Box display={{ xs: "flex", md: "none" }} gap="5px" alignItems="center" flexWrap="wrap">
                        {video.expired && <CustomChip color="error" tooltipPlacement='bottom' variant='semifilled' value={`Expired At: ${dayjs( video.endDate ).format( "DD/MM/YYYY, HH:mm" )}`} />}
                        {!video.expired && <CustomChip color="error" tooltipPlacement='bottom' tooltipTitle='Students should watch this video before this time to get marked as present for the class.' value={`Exp: ${dayjs( video.endDate ).format( "LLL" )}`} />}
                    </Box>
                </Box>
            </Box >
        </Box>
    )
} )

export default HybridClassVideo